import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import { LanguageToggle } from '@/i18n/LanguageToggle';
import type { Permission } from '@/api/types';
import type { TranslationKey } from '@/i18n/dictionaries/en';

/**
 * The 12 sections from the ADR-064 epic spec, plus ONE new section
 * (Integrations) added by the Marketplace Shared Foundation epic, whose
 * own brief explicitly calls for a genuinely new Office UI surface for
 * channel connections/credentials/sync status - not a violation of the
 * "no more" note this comment used to carry, a deliberate, epic-directed
 * addition. A section is only hidden (not disabled) when the caller's
 * role has none of the permissions it needs at all - Overview, Problems
 * and Settings are always visible since every role may open them
 * (Problems scopes itself server-side).
 */
const NAV: { to: string; labelKey: TranslationKey; permission?: Permission; anyPermission?: readonly Permission[] }[] = [
  { to: '/', labelKey: 'nav.overview' },
  { to: '/onboarding', labelKey: 'nav.onboarding', permission: 'ONBOARDING.READ' },
  { to: '/queue', labelKey: 'nav.queue' },
  { to: '/sellers', labelKey: 'nav.sellers', permission: 'SELLER.READ' },
  { to: '/inventory', labelKey: 'nav.inventory', permission: 'INVENTORY.READ' },
  { to: '/inbounds', labelKey: 'nav.inbounds', permission: 'INVENTORY.READ' },
  { to: '/putaway', labelKey: 'nav.putaway', permission: 'INVENTORY.READ' },
  { to: '/shipments', labelKey: 'nav.shipments', permission: 'ORDERS.READ' },
  { to: '/returns', labelKey: 'nav.returns', permission: 'INVENTORY.READ' },
  { to: '/billing', labelKey: 'nav.billing', permission: 'BILLING.READ' },
  { to: '/imports', labelKey: 'nav.imports', permission: 'IMPORT.READ' },
  { to: '/warehouses', labelKey: 'nav.warehouses', permission: 'REFERENCE.READ' },
  { to: '/warehouse/move-scan', labelKey: 'nav.moveScan', permission: 'INVENTORY.WRITE' },
  { to: '/users', labelKey: 'nav.users', permission: 'USERS.READ' },
  { to: '/channels', labelKey: 'nav.integrations', permission: 'CHANNELS.READ' },
  // ADR-077: gated exactly like Billing - a warehouse or picker role never
  // sees it, and the server refuses it independently if they navigate to it.
  { to: '/subscription', labelKey: 'nav.subscription', permission: 'SUBSCRIPTION.READ' },
  { to: '/problems', labelKey: 'nav.problems' },
  // Pilot exceptions: stock that needs a count, short order lines, correction history.
  { to: '/exceptions', labelKey: 'nav.exceptions', anyPermission: ['INVENTORY.READ', 'ORDERS.READ'] },
  { to: '/settings', labelKey: 'nav.settings' },
];

export function AppLayout(): JSX.Element {
  const { state, hasPermission, logout } = useAuth();
  const t = useT();
  const self = state.status === 'authenticated' ? state.self : null;
  // Off-canvas drawer below the app-shell's ~860px breakpoint (styles.css) -
  // above it, CSS overrides make .app-nav a normal static sidebar and this
  // state is simply unused (the topbar/backdrop stay display:none there).
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        {t('nav.skipToContent')}
      </a>
      <div className="app-topbar">
        <div className="brand">{t('nav.brand')}</div>
        <button
          type="button"
          className="nav-toggle"
          aria-label={navOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div className={`nav-backdrop${navOpen ? ' open' : ''}`} onClick={() => setNavOpen(false)} />
      <nav className={`app-nav${navOpen ? ' open' : ''}`} aria-label="Primary">
        <div className="brand">{t('nav.brand')}</div>
        {NAV.filter((item) =>
          item.anyPermission ? item.anyPermission.some((p) => hasPermission(p)) : !item.permission || hasPermission(item.permission),
        ).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={() => setNavOpen(false)}
          >
            {t(item.labelKey)}
          </NavLink>
        ))}
        <LanguageToggle />
        {self && (
          <div className="who">
            <div>{self.role}</div>
            <button type="button" onClick={logout} style={{ marginTop: 8 }}>
              {t('nav.logOut')}
            </button>
          </div>
        )}
      </nav>
      <main className="app-main" id="main-content">
        <Outlet />
      </main>
    </div>
  );
}
